import React, { memo, useEffect } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Grid,
    Card,
    Typography,
    Button,
    Checkbox,
    FormControlLabel,
    Divider,
    IconButton,
    Autocomplete,
    TextField,
    CircularProgress
} from "@mui/material";
import {
    CheckCircle,
    Check,
    Logout,
    ArrowBack,
    CloudUploadOutlined
} from "@mui/icons-material";
import { useJsApiLoader } from "@react-google-maps/api";
import usePlacesAutocomplete, { getGeocode, getLatLng } from "use-places-autocomplete";

import CustomLabelForm from "components/Fileds/CustomLabelForm";
import CustomTextField from "components/Fileds/CustomTextFiled";
import CustomButton from "components/Fileds/CustomButton";
import CustomMultilineField from "components/Fileds/CustomTextArea";
import CustomPhoneContryCodeInput from "components/Fileds/CustomPhoneContryCodeInput";

import Logo from "assests/SL-POS (1).svg";
import { countries } from "utils/countries";
import { useDropzone } from "react-dropzone";
import "./style.css";

const libraries = ['places'];

// Inner Custom Dropzone component
// const DropzoneBox = ({ type, caption, previewImage, handleFileUpload }) => {
//     const onDrop = (acceptedFiles) => {
//         if (acceptedFiles && acceptedFiles.length > 0) {
//             handleFileUpload(type, acceptedFiles);
//         }
//     };

//     const { getRootProps, getInputProps, isDragActive } = useDropzone({
//         accept: {
//             'image/jpeg': ['.jpg', '.jpeg'],
//             'image/png': ['.png']
//         },
//         onDrop
//     });

//     return (
//         <Box {...getRootProps()} className="onboarding-dropzone-wrapper">
//             <input {...getInputProps()} />
//             {previewImage ? (
//                 <Box className="onboarding-dropzone-preview-box">
//                     <img
//                         src={
//                             previewImage.startsWith('blob')
//                                 ? previewImage
//                                 : `${process.env.REACT_APP_API_ROUTE}/files/${previewImage}`
//                         }
//                         alt="preview"
//                     />
//                 </Box>
//             ) : (
//                 <Box className="onboarding-dropzone-preview-box" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//                     <CloudUploadOutlined sx={{ fontSize: 32, color: '#888' }} />
//                 </Box>
//             )}
//             <Typography className="onboarding-dropzone-text">
//                 {isDragActive ? "Drop the image here" : "Drag & drop image here, or click to select file"}
//             </Typography>
//             <Typography className="onboarding-dropzone-caption">
//                 {caption}
//             </Typography>
//         </Box>
//     );
// };
const DropzoneBox = ({ type, caption, previewImage, handleFileUpload }) => {
    const onDrop = (acceptedFiles) => {
        if (acceptedFiles && acceptedFiles.length > 0) {
            handleFileUpload(type, acceptedFiles);
        }
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: {
            "image/jpeg": [".jpg", ".jpeg"],
            "image/png": [".png"]
        },
        onDrop
    });

    return (
        <Box {...getRootProps()} className="onboarding-dropzone-wrapper">
            <input {...getInputProps()} />

            <Box className="onboarding-dropzone-preview-box">
                {previewImage ? (
                    <img
                        src={
                            previewImage.startsWith("blob")
                                ? previewImage
                                : `${process.env.REACT_APP_API_ROUTE}/files/${previewImage}`
                        }
                        alt="preview"
                    />
                ) : (
                    <Box className="onboarding-dropzone-placeholder">
                        <Typography>
                            Drag & drop image here,
                            <br />
                            or click to select file
                        </Typography>
                    </Box>
                )}
            </Box>

            {caption && (
                <Typography className="onboarding-dropzone-caption">
                    {caption}
                </Typography>
            )}
        </Box>
    );
};

const OnboardingContainer = memo(({
    currentStep,
    setCurrentStep,
    formik,
    handleFileUpload,
    handleLogout,
    isSubmittingProfile,
    isSuccessScreen
}) => {
    const {
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        setFieldTouched
    } = formik;

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "AIzaSyDl33GnWibNPt7vrbnU1tBFNwQ4V98aChc",
        libraries,
    });

    const {
        ready,
        value: placeValue,
        suggestions: { status, data },
        setValue: setPlaceValue,
        clearSuggestions,
        init,
    } = usePlacesAutocomplete({
        initOnMount: false,
        debounce: 300,
    });

    useEffect(() => {
        if (isLoaded) {
            init();
        }
    }, [isLoaded, init]);

    const handleSelectLocation = async (newValue) => {
        if (!newValue) {
            setFieldValue("title", "");
            return;
        }

        const val = typeof newValue === 'string' ? newValue : newValue.description;
        setFieldValue("title", val);
        setPlaceValue(val, false);
        clearSuggestions();

        try {
            const results = await getGeocode({ address: val });
            if (results && results.length > 0) {
                const { lat, lng } = await getLatLng(results[0]);
                setFieldValue("lat", lat.toString());
                setFieldValue("long", lng.toString());

                let street_number = '';
                let route = '';
                let city = '';
                let state = '';
                let countryName = '';
                let postal_code = '';

                results[0].address_components.forEach(component => {
                    const types = component.types;
                    if (types.includes('street_number')) {
                        street_number = component.long_name;
                    }
                    if (types.includes('route')) {
                        route = component.long_name;
                    }
                    if (types.includes('locality') || types.includes("postal_town")) {
                        city = component.long_name;
                    }
                    if (types.includes('administrative_area_level_1')) {
                        state = component.long_name;
                    }
                    if (types.includes('country')) {
                        countryName = component.long_name;
                    }
                    if (types.includes('postal_code')) {
                        postal_code = component.long_name;
                    }
                });

                const addressLine1 = [street_number, route].filter(Boolean).join(' ');
                if (addressLine1) setFieldValue("address", addressLine1);
                if (city) setFieldValue("city", city);
                if (state) setFieldValue("state", state);
                if (postal_code) setFieldValue("postal_code", postal_code);

                if (countryName) {
                    const countryObj = countries.find(c => c.label.toLowerCase() === countryName.toLowerCase() || c.code.toLowerCase() === countryName.toLowerCase());
                    if (countryObj) {
                        setFieldValue("country", countryObj.label);
                        setFieldValue("country_code", countryObj.country_code);
                        setFieldValue("country_ISO", countryObj.code);
                    } else {
                        setFieldValue("country", countryName);
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching geocode: ", error);
        }
    };

    // Navigation and validation helpers per step
    const handleNextStep = async () => {
        if (currentStep === 2) {
            // Validate Step 2 fields
            setFieldTouched("image", true);
            setFieldTouched("service_tax", true);
            if (values.is_vat_enabled) {
                setFieldTouched("vat_number", true);
            }

            // Check if there are errors in Step 2 fields
            const step2Errors = {};
            if (!values.image) step2Errors.image = "Profile Image is required";
            if (!values.service_tax) step2Errors.service_tax = "Service Tax is required";
            if (values.is_vat_enabled && !values.vat_number) step2Errors.vat_number = "VAT Number is required";

            if (Object.keys(step2Errors).length === 0) {
                setCurrentStep(3);
            }
        } else if (currentStep === 3) {
            // Validate Step 3 fields
            setFieldTouched("title", true);
            setFieldTouched("address", true);
            setFieldTouched("city", true);
            setFieldTouched("state", true);
            setFieldTouched("country", true);
            setFieldTouched("postal_code", true);

            const step3Errors = {};
            if (!values.title) step3Errors.title = "Premises Name is required";
            if (!values.address) step3Errors.address = "Address Line 1 is required";
            if (!values.city) step3Errors.city = "City is required";
            if (!values.state) step3Errors.state = "State is required";
            if (!values.country) step3Errors.country = "Country is required";
            if (!values.postal_code) step3Errors.postal_code = "Postal Code is required";

            if (Object.keys(step3Errors).length === 0) {
                setCurrentStep(4);
            }
        }
    };

    const handlePrevStep = () => {
        if (currentStep > 2) {
            setCurrentStep(currentStep - 1);
        }
    };

    return (
        <Box className="onboarding-bg">
            {/* Dark Header Banner */}
            <Box className="onboarding-header">
                <img src={Logo} alt="logo" className="onboarding-logo" />
                <Typography variant="h5" className="onboarding-welcome-title">
                    Welcome to POS for {values.name || "your Restaurant"}
                </Typography>
                <Typography className="onboarding-welcome-subtitle">
                    Few more steps and you're all set!
                </Typography>
                <Button
                    className="onboarding-logout-btn"
                    onClick={handleLogout}
                    startIcon={<Logout />}
                >
                    Logout
                </Button>
            </Box>

            {/* Central Card Form */}
            <Card className="onboarding-card">
                <Grid container spacing={4}>

                    {/* Left Column: Vertical Stepper */}
                    <Grid item xs={12} md={4}>
                        <Box className="onboarding-stepper-container">

                            {/* Step 1: Admin */}
                            <Box className="stepper-step completed">
                                <Box className="stepper-icon">
                                    <Check sx={{ fontSize: 18 }} />
                                </Box>
                                <Box className="stepper-label">
                                    Account created by Admin
                                </Box>
                            </Box>

                            {/* Step 2: Restaurant Details */}
                            <Box className={`stepper-step ${currentStep === 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
                                <Box className="stepper-icon">
                                    {currentStep > 2 ? <Check sx={{ fontSize: 18 }} /> : "2"}
                                </Box>
                                <Box className="stepper-label">
                                    Add Restaurant Details
                                </Box>
                            </Box>

                            {/* Step 3: Location Details */}
                            <Box className={`stepper-step ${currentStep === 3 ? 'active' : ''} ${currentStep > 3 ? 'completed' : ''}`}>
                                <Box className="stepper-icon">
                                    {currentStep > 3 ? <Check sx={{ fontSize: 18 }} /> : "3"}
                                </Box>
                                <Box className="stepper-label">
                                    Add Location Details
                                </Box>
                            </Box>

                            {/* Step 4: Contact Details */}
                            <Box className={`stepper-step ${currentStep === 4 ? 'active' : ''} ${isSuccessScreen ? 'completed' : ''}`}>
                                <Box className="stepper-icon">
                                    {isSuccessScreen ? <Check sx={{ fontSize: 18 }} /> : "4"}
                                </Box>
                                <Box className="stepper-label">
                                    Add Contact Details
                                </Box>
                            </Box>

                            {/* Step 5: All Set Success indicator */}
                            <Box className={`stepper-step last-step ${isSuccessScreen ? 'success-stepper completed' : ''}`}>
                                <Box className="stepper-icon">
                                    {isSuccessScreen ? <Check sx={{ fontSize: 18 }} /> : "5"}
                                </Box>
                                <Box className="stepper-label">
                                    All set!
                                </Box>
                            </Box>

                            <Typography className="onboarding-stepper-footer-text">
                                You can always update the allowed details later in Profile.
                            </Typography>
                        </Box>
                    </Grid>

                    {/* Right Column: Step Forms */}
                    <Grid item xs={12} md={8} sx={{ display: 'flex', flexDirection: 'column' }}>
                        {isSuccessScreen ? (
                            /* Step 5: Success Screen & Animation */
                            <Box className="success-screen-wrapper">
                                <div className="checkmark-circle">
                                    <div className="background"></div>
                                    <div className="checkmark draw"></div>
                                </div>
                                <Typography className="onboarding-success-title">
                                    Successfully Updated Profile!
                                </Typography>
                                <Typography className="onboarding-success-subtitle">
                                    Preparing your workspace, redirecting in 2 seconds...
                                </Typography>
                            </Box>
                        ) : (
                            <form onSubmit={handleSubmit} style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <Box sx={{ flexGrow: 1 }}>

                                    {/* Step 2 Form Content */}
                                    {currentStep === 2 && (
                                        <Grid container spacing={3}>
                                            {/* Images side-by-side */}
                                            <Grid item xs={12} sm={6}>
                                                <CustomLabelForm variant="body2" label="Restaurant Logo/Profile Image *" />
                                                <DropzoneBox
                                                    type="image"
                                                    caption="Recommended ratio is 1:1. Supported formats: PNG, JPG, and JPEG. Max file size: 10MB."
                                                    previewImage={values.image}
                                                    handleFileUpload={handleFileUpload}
                                                />
                                                {touched.image && errors.image && (
                                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                                        {errors.image}
                                                    </Typography>
                                                )}
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <CustomLabelForm variant="body2" label="Restaurant Banner Image" />
                                                <DropzoneBox
                                                    type="banner_image"
                                                    caption="Recommended ratio is 100:63. Supported formats: PNG, JPG, and JPEG. Max file size: 10MB."
                                                    previewImage={values.banner_image}
                                                    handleFileUpload={handleFileUpload}
                                                />
                                                {touched.banner_image && errors.banner_image && (
                                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                                        {errors.banner_image}
                                                    </Typography>
                                                )}
                                            </Grid>



                                            {/* Tagline & Website */}
                                            <Grid item xs={12} sm={6}>
                                                <CustomLabelForm variant="body2" label="Tagline (Max 30 characters)" />
                                                <CustomTextField
                                                    name="tagline"
                                                    value={values.tagline}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.tagline && errors.tagline}
                                                    helperText={touched.tagline && errors.tagline}
                                                    placeholder="Enter tagline here"
                                                    inputProps={{ maxLength: 30 }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <CustomLabelForm variant="body2" label="Website" />
                                                <CustomTextField
                                                    name="restaurant_website"
                                                    value={values.restaurant_website}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.restaurant_website && errors.restaurant_website}
                                                    helperText={touched.restaurant_website && errors.restaurant_website}
                                                    placeholder="www.example.com"
                                                    type="url"
                                                />
                                            </Grid>

                                            {/* Bio & Service Tax */}
                                            <Grid item xs={12} sm={6}>
                                                <CustomLabelForm variant="body2" label="Bio (About Restaurant/Cafe)" />
                                                <CustomMultilineField
                                                    name="description"
                                                    value={values.description}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    placeholder="Enter your bio here"
                                                    maxLength={250}
                                                    rows={3}
                                                    showError={touched.description && errors.description}
                                                    errorText={touched.description && errors.description}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <CustomLabelForm variant="body2" label="Service Tax *" />
                                                <CustomTextField
                                                    name="service_tax"
                                                    value={values.service_tax}
                                                    onChange={(e) => {
                                                        if (/^\d*\.?\d*$/.test(e.target.value)) handleChange(e);
                                                    }}
                                                    onBlur={handleBlur}
                                                    error={touched.service_tax && errors.service_tax}
                                                    helperText={touched.service_tax && errors.service_tax}
                                                    placeholder="Enter service tax % here"
                                                    InputProps={{
                                                        startAdornment: <Box sx={{ mr: 1, color: 'text.secondary' }}>%</Box>
                                                    }}
                                                />
                                            </Grid>

                                            {/* VAT Checkbox & VAT Number Field */}
                                            <Grid item xs={12}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={values.is_vat_enabled}
                                                            onChange={(e) => setFieldValue("is_vat_enabled", e.target.checked)}
                                                            name="is_vat_enabled"
                                                        />
                                                    }
                                                    label="VAT Number Available"
                                                />
                                            </Grid>

                                            {values.is_vat_enabled && (
                                                <Grid item xs={12}>
                                                    <CustomLabelForm variant="body2" label="VAT Number *" />
                                                    <CustomTextField
                                                        name="vat_number"
                                                        value={values.vat_number}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        error={touched.vat_number && errors.vat_number}
                                                        helperText={touched.vat_number && errors.vat_number}
                                                        placeholder="Enter your VAT Number here"
                                                    />
                                                </Grid>
                                            )}
                                        </Grid>
                                    )}

                                    {/* Step 3 Form Content */}
                                    {currentStep === 3 && (
                                        <Grid container spacing={3}>
                                            {/* Google Autocomplete Premises Name */}
                                            <Grid item xs={12}>
                                                <CustomLabelForm variant="body2" label="Premises/Building Name *" />
                                                <Autocomplete
                                                    id="onboarding-places"
                                                    options={data.map((suggestion) => ({ description: suggestion.description, place_id: suggestion.place_id }))}
                                                    disabled={!ready}
                                                    value={values.title ? { description: values.title } : null}
                                                    filterOptions={(x) => x}
                                                    getOptionLabel={(option) => {
                                                        if (typeof option === 'string') return option;
                                                        return option.description || '';
                                                    }}
                                                    onInputChange={(e, val, reason) => {
                                                        setPlaceValue(val);
                                                        setFieldValue("title", val);

                                                        if (reason === "clear" || val.trim() === "") {
                                                            setFieldValue("address", "");
                                                            setFieldValue("address_line2", "");
                                                            setFieldValue("city", "");
                                                            setFieldValue("state", "");
                                                            setFieldValue("postal_code", "");
                                                            setFieldValue("lat", "");
                                                            setFieldValue("long", "");
                                                            setFieldValue("country", "");
                                                            setFieldValue("country_code", "");
                                                            setFieldValue("country_ISO", "");
                                                        }
                                                    }}
                                                    onChange={(event, newValue) => {
                                                        handleSelectLocation(newValue);
                                                    }}
                                                    onBlur={() => {
                                                        setFieldTouched("title", true);
                                                        clearSuggestions();
                                                    }}
                                                    freeSolo
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            name="title"
                                                            error={touched.title && Boolean(errors.title)}
                                                            helperText={touched.title && errors.title}
                                                            placeholder="Type Building or Premise name (Autocompletes with Google Places)"
                                                            fullWidth
                                                            variant="outlined"
                                                            sx={{
                                                                "& .MuiOutlinedInput-root": {
                                                                    height: 45,
                                                                    borderRadius: "10px",
                                                                    fontSize: "14px",
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Grid>

                                            {/* Address Line 2 & Address Line 1 */}
                                            <Grid item xs={12} sm={6}>
                                                <CustomLabelForm variant="body2" label="Address Line 1 *" />
                                                <CustomTextField
                                                    name="address"
                                                    value={values.address}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.address && errors.address}
                                                    helperText={touched.address && errors.address}
                                                    placeholder="Enter Address line 1"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <CustomLabelForm variant="body2" label="Address Line 2" />
                                                <CustomTextField
                                                    name="address_line2"
                                                    value={values.address_line2}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.address_line2 && errors.address_line2}
                                                    helperText={touched.address_line2 && errors.address_line2}
                                                    placeholder="Enter Address line 2"
                                                />
                                            </Grid>

                                            {/* City & State */}
                                            <Grid item xs={12} sm={6}>
                                                <CustomLabelForm variant="body2" label="City *" />
                                                <CustomTextField
                                                    name="city"
                                                    value={values.city}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.city && errors.city}
                                                    helperText={touched.city && errors.city}
                                                    placeholder="Enter City name here"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <CustomLabelForm variant="body2" label="State *" />
                                                <CustomTextField
                                                    name="state"
                                                    value={values.state}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.state && errors.state}
                                                    helperText={touched.state && errors.state}
                                                    placeholder="Enter State name here"
                                                />
                                            </Grid>

                                            {/* Country & Postal Code */}
                                            <Grid item xs={12} sm={6}>
                                                <CustomLabelForm variant="body2" label="Country *" />
                                                <Autocomplete
                                                    options={countries}
                                                    value={countries.find((c) => c.label === values.country) || null}
                                                    onChange={(e, newValue) => {
                                                        setFieldValue("country", newValue ? newValue.label : "");
                                                        setFieldValue("country_code", newValue ? newValue.country_code : "");
                                                        setFieldValue("country_ISO", newValue ? newValue.code : "");
                                                    }}
                                                    getOptionLabel={(o) => o.label}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            error={touched.country && Boolean(errors.country)}
                                                            helperText={touched.country && errors.country}
                                                            placeholder="Select Country"
                                                            variant="outlined"
                                                            sx={{
                                                                "& .MuiOutlinedInput-root": {
                                                                    height: 45,
                                                                    borderRadius: "10px",
                                                                    fontSize: "14px",
                                                                },
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <CustomLabelForm variant="body2" label="Postal Code *" />
                                                <CustomTextField
                                                    name="postal_code"
                                                    value={values.postal_code}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.postal_code && errors.postal_code}
                                                    helperText={touched.postal_code && errors.postal_code}
                                                    placeholder="Zipcode/Postalcode"
                                                />
                                            </Grid>

                                            {/* Lat & Long */}
                                            <Grid item xs={12} sm={6}>
                                                <CustomLabelForm variant="body2" label="Latitude" />
                                                <CustomTextField
                                                    name="lat"
                                                    value={values.lat}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.lat && errors.lat}
                                                    helperText={touched.lat && errors.lat}
                                                    placeholder="Enter latitude value"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <CustomLabelForm variant="body2" label="Longitude" />
                                                <CustomTextField
                                                    name="long"
                                                    value={values.long}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.long && errors.long}
                                                    helperText={touched.long && errors.long}
                                                    placeholder="Enter longitude value"
                                                />
                                            </Grid>
                                        </Grid>
                                    )}

                                    {/* Step 4 Form Content */}
                                    {currentStep === 4 && (
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} sm={6}>
                                                <CustomLabelForm variant="body2" label="Owner's Full Name *" />
                                                <CustomTextField
                                                    name="full_name"
                                                    value={values.full_name}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.full_name && errors.full_name}
                                                    helperText={touched.full_name && errors.full_name}
                                                    placeholder="Enter Owner's Full Name"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <CustomLabelForm variant="body2" label="Owner's Email *" />
                                                <CustomTextField
                                                    name="personal_email"
                                                    value={values.personal_email}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    error={touched.personal_email && errors.personal_email}
                                                    helperText={touched.personal_email && errors.personal_email}
                                                    placeholder="Enter Owner's Email"
                                                    type="email"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <CustomLabelForm variant="body2" label="Owner's Contact Number *" />
                                                <CustomPhoneContryCodeInput
                                                    value={values.phone}
                                                    countryCode={values?.country_code?.toString().replace(/^\+/, "")}
                                                    country_ISO={values?.country_ISO}
                                                    placeholder="Enter Contact Number"
                                                    error={Boolean(touched.phone && errors.phone)}
                                                    helperText={touched.phone && errors.phone}
                                                    onChange={(e) => {
                                                        setFieldValue("phone", e.target.value.replace(/\D/g, ""))
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <CustomLabelForm variant="body2" label="Owner's Alternate Contact Number" />
                                                <CustomPhoneContryCodeInput
                                                    value={values.alternate_contact_number}
                                                    countryCode={values?.country_code?.toString().replace(/^\+/, "")}
                                                    country_ISO={values?.country_ISO}
                                                    placeholder="Enter Alternate Contact Number"
                                                    error={Boolean(touched.alternate_contact_number && errors.alternate_contact_number)}
                                                    helperText={touched.alternate_contact_number && errors.alternate_contact_number}
                                                    onChange={(e) => {
                                                        setFieldValue("alternate_contact_number", e.target.value.replace(/\D/g, ""))
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    )}

                                </Box>

                                {/* Action Buttons Footer */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2}}>
                                    {currentStep > 2 ? (
                                        <Button
                                            variant="outlined"
                                            onClick={handlePrevStep}
                                            startIcon={<ArrowBack />}
                                            sx={{
                                                textTransform: 'none',
                                                borderColor: '#b0bec5',
                                                color: '#374151',
                                                borderRadius: '8px',
                                                height: '45px',
                                                px: 3,
                                                '&:hover': {
                                                    borderColor: '#374151',
                                                    backgroundColor: '#f7f9fa'
                                                }
                                            }}
                                        >
                                            Go Back
                                        </Button>
                                    ) : (
                                        <Box /> /* Spacer if no back button */
                                    )}

                                    {currentStep < 4 ? (
                                        <CustomButton
                                            type="button"
                                            onClick={handleNextStep}
                                            width="150px"
                                            height="45px"
                                            className="login-form-btn"
                                            sx={{
                                                backgroundColor: '#111111',
                                                color: '#ffffff',
                                                borderRadius: '8px',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                '&:hover': {
                                                    backgroundColor: '#222222'
                                                }
                                            }}
                                        >
                                            Save
                                        </CustomButton>
                                    ) : (
                                        <CustomButton
                                            type="submit"
                                            loading={isSubmittingProfile}
                                            width="150px"
                                            height="45px"
                                            className="login-form-btn"
                                            sx={{
                                                backgroundColor: '#111111',
                                                color: '#ffffff',
                                                borderRadius: '8px',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                '&:hover': {
                                                    backgroundColor: '#222222'
                                                }
                                            }}
                                        >
                                            Save
                                        </CustomButton>
                                    )}
                                </Box>
                            </form>
                        )}
                    </Grid>

                </Grid>
            </Card>
        </Box>
    );
});

OnboardingContainer.propTypes = {
    currentStep: PropTypes.number.isRequired,
    setCurrentStep: PropTypes.func.isRequired,
    formik: PropTypes.object.isRequired,
    handleFileUpload: PropTypes.func.isRequired,
    handleLogout: PropTypes.func.isRequired,
    isSubmittingProfile: PropTypes.bool.isRequired,
    isSuccessScreen: PropTypes.bool.isRequired,
};

export default OnboardingContainer;
